<template>
  <div class="v-csv-uploader">
    <v-dropzone v-if="enableDropzone" @dropped="parse($event)" :errorChecker="dropzoneErrorChecker">
      <template v-slot:instructions>
        <slot name="dropzone-instructions"/>
      </template>
    </v-dropzone>
    <input class="d-none"
           type="file"
           :ref="inputRef"
           @change="parse($event.target.files)"
           accept="text/csv,text/plain"/>
    <v-btn :color="buttonColor"
           :ref="buttonRef"
           :loading="parsing"
           @click.native="$refs[inputRef].click()"
           class="mr-2">
      <v-icon left>{{ buttonIcon }}</v-icon>
      {{ buttonText }}
    </v-btn>
    <v-csv-dialog v-if="dialog"
                  v-model="file"
                  :fields="fields"
                  :hide-per-row-menu="hidePerRowMenu"
                  @close="close"
                  :data-test-attrs="`${$attrs['data-test']}`">
      <template v-slot:title>
        <slot name="dialog-title" v-bind="{ filename: file.name }"/>
      </template>
      <template v-slot:instructions="validations">
        <slot name="dialog-instructions" v-bind="validations"/>
      </template>
      <template v-slot:csv-header-cell="header">
        <slot name="dialog-header-cell" v-bind="header"></slot>
      </template>
    </v-csv-dialog>
    <v-csv-errors :files="errors" @close="errors = []" />
  </div>
</template>

<script>
import VCsvDialog from './CsvDialog'
import VCsvErrors from './CsvErrors'
import VDropzone from '@/components/Dropzone'
import { defaultParser } from '@/services/csv'

export default {
  name: 'CsvUploader',
  components: {
    VCsvDialog,
    VCsvErrors,
    VDropzone
  },
  props: {
    // Upload button text
    buttonText: {
      type: String,
      default: 'Upload CSV File',
      required: false
    },
    // Upload button icon
    buttonIcon: {
      type: String,
      default: 'cloud_upload',
      required: false
    },
    // Upload button color
    buttonColor: {
      type: String,
      default: 'primary',
      required: false
    },
    // Does the CSV file has a header
    hasHeaders: {
      type: Boolean,
      default: true,
      required: false
    },
    // Enable dropzone feature
    enableDropzone: {
      type: Boolean,
      default: true,
      required: false
    },
    // CSV Parser
    parser: {
      type: Function,
      default (file, hasHeaders) {
        return defaultParser(file, hasHeaders)
      },
      required: false
    },
    // Fields to match to if provided
    fields: {
      type: Array,
      default () {
        return []
      },
      required: false
    },
    hidePerRowMenu: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data () {
    return {
      parsing: false,
      dialog: false,
      errors: [],
      file: {
        name: null,
        headers: [],
        items: []
      }
    }
  },
  computed: {
    /**
     * Unique ref property of input field
     */
    inputRef () {
      return `csv-uploader-input-${this._uid}`
    },
    /**
     * Unique ref property of input field
     */
    buttonRef () {
      return `csv-uploader-button-${this._uid}`
    }
  },
  methods: {
    /**
     * Parse selected csv file
     */
    async parse (files) {
      if (Array.from(files).length !== 1) {
        let error = new Error('accept only one file per upload!')
        error.name = 'Multiple Files'
        console.error(error)

        this.$set(this, 'errors', [{
          name: 'General',
          errors: [ error ]
        }])
        return
      }
      this.$set(this, 'parsing', true)

      this.parser(files[0], this.hasHeaders)
        .then(({ headers, items, name }) => {
          this.$set(this, 'file', {
            name,
            headers,
            items
          })
        })
        .then(() => {
          // Map predefined field headers
          this.fields.forEach(field => {
            this.file.headers.some(header => {
              // If a match exists, assign its value and exit the some
              if (field.searchIn.indexOf(header.value.toLowerCase()) !== -1) {
                header['field'] = field.name
                return true
              }
            })
          })
        })
        .then(() => {
          this.$set(this, 'dialog', true)
        })
        .catch(error => {
          this.$set(this, 'errors', [error])
        })
        .finally(() => {
          this.$set(this, 'parsing', false)
        })
    },
    /**
     * Populating CSV file
     */
    close (isSave) {
      if (isSave) {
        this.$emit('parsed', this.file)
      }

      this.$set(this, 'dialog', false)
      this.clear()
    },
    /**
     * Clear csv file
     */
    clear () {
      this.$set(this, 'file', {
        name: null,
        headers: [],
        items: []
      })

      this.$refs[this.inputRef].value = ''
    },
    /**
     * Run before all dropzone event handlers
     */
    dropzoneErrorChecker (event) {
      // Alert user if he dropped file\s if there is an open dialog
      if (this.dialog && event.type === 'drop') {
        alert('There\'s an open CSV dialog. Please, close it first to upload a new one!')
      }

      // Stop event handler if there is an open dialog
      return this.dialog
    }
  }
}
</script>
